import React, { useEffect, useCallback } from 'react';
import { Link, Route, withRouter, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Tab, TabList, Tabs, TabPanel } from '@nextiva/next-ui';
import { useFeature } from '@nextiva/feature-toggle';
import { billingLandingRoutes } from './BillingLanding.routes';

import {
  Base,
  PaymentDrawer,
  MakePayment,
  Location,
  AlertNotifications,
} from 'components';
import { MOONSHOT_OUTAGE_FLAG, PB_MULTI_SITE_FLAG } from 'constant';
import { isAdmin, checkBillingPermission } from 'utils';
import { parseLocationForDropdown } from 'parsers';

import './BillingLanding.scss';

const blockName = 'billing-landing';

const BillingLanding = props => {
  const { SUMMARY, PAYMENT, INVOICES } = billingLandingRoutes;
  const history = useHistory();
  const {
    activeLocation,
    addActiveLocation,
    clearAlerts,
    data: {
      roles,
      permissions,
      user: {
        profileDTO: { corpAcctNbr },
      },
    },
    isPaymentMethodCapabilityEnabled,
    locations,
    startAddNextivaBill,
    setPaymentDisabled,
    setPaymentMethodCapabilityEnabled,
  } = props;

  const MAKE_PAYMENT_TIMER = 30_000;
  const { isEnabled: moonshotOutage } = useFeature({
    name: MOONSHOT_OUTAGE_FLAG,
  });
  const {
    isEnabled: isMultiSiteEnabled,
    isReady: isMultiSiteReady,
  } = useFeature({
    name: PB_MULTI_SITE_FLAG,
  });

  /**
   * Logic required to display Location dropdown
   *
   * @returns {boolean} to display or not the component
   */
  const displayLocationDropdown = () => {
    const condition = locations.length > 1;
    return isMultiSiteReady ? isMultiSiteEnabled && condition : condition;
  };

  useEffect(() => {
    if (locations?.length === 1) {
      addActiveLocation(parseLocationForDropdown(locations[0]));
    }
    if (locations?.length > 1) {
      const defaultLocation = locations.filter(
        location => location.defaultBillingSite
      );
      if (defaultLocation.length > 0) {
        addActiveLocation(parseLocationForDropdown(defaultLocation[0]));
      } else {
        addActiveLocation(parseLocationForDropdown(locations[0]));
      }
    }
  }, [addActiveLocation, locations]);

  /**
   * Update active location with the element selected in dropdown
   *
   * @param {{locationId: string; label: string;}} selected - selected location in dropdown
   */
  const locationChange = useCallback(
    (_, selected) => {
      if (selected) {
        clearAlerts();
        addActiveLocation(selected);
      }
    },
    [addActiveLocation, clearAlerts]
  );

  const getActiveIndex = () => {
    return Object.values(billingLandingRoutes).findIndex(({ path }) => {
      return props.location.pathname.includes(path);
    });
  };

  /**
   * Get billing info after a executing a timer
   */
  const startBillingTimer = useCallback(() => {
    const timer = setTimeout(
      () => startAddNextivaBill(props.activeLocation.locationId),
      MAKE_PAYMENT_TIMER
    );
    return () => clearTimeout(timer);
  }, [startAddNextivaBill, props.activeLocation.locationId]);

  /**
   * Disable Make Payment button and refresh billing info after a period of time
   */
  const refreshBillingInfo = useCallback(async () => {
    await setPaymentDisabled(true);
    await setPaymentMethodCapabilityEnabled(false);
    startBillingTimer();
  }, [
    setPaymentDisabled,
    setPaymentMethodCapabilityEnabled,
    startBillingTimer,
  ]);

  const renderApp = () => {
    if (isAdmin(roles) || checkBillingPermission(permissions)) {
      return (
        <>
          {props.isLoading && (
            <div className={`${blockName}__spinner`}>
              <CircularProgress />
            </div>
          )}

          <AlertNotifications
            alertDetails={props.alertDetails}
            clearAlertDetails={props.clearAlertDetails}
            clearCustomError={props.clearCustomError}
            clearGlobalError={props.clearGlobalError}
            clearGlobalWarning={props.clearGlobalWarning}
            customError={props.customError}
            error={props.error}
            globalWarning={props.globalWarning}
          />

          {displayLocationDropdown() && (
            <Location
              activeLocation={activeLocation}
              locationChange={locationChange}
              locations={locations}
            />
          )}

          <Base projectName="Billing">
            <Tabs
              className={`${blockName}__tabs`}
              selectedIndex={getActiveIndex()}
              onSelect={() => {}}
            >
              <TabList className="automation-tabs-navigation">
                <Tab className="automation-tab-navigation">
                  <Link to={SUMMARY.path}>{SUMMARY.name}</Link>
                </Tab>
                <Tab className="automation-tab-navigation">
                  <Link to={INVOICES.path}>{INVOICES.name}</Link>
                </Tab>
                {!moonshotOutage && isPaymentMethodCapabilityEnabled && (
                  <Tab className="automation-tab-navigation">
                    <Link to={PAYMENT.path}>{PAYMENT.name}</Link>
                  </Tab>
                )}
                <Tab className="automation-tab-navigation">
                  <a href="/apps/platform-admin/#/licensing/licensing">
                    Licenses
                  </a>
                </Tab>
              </TabList>
              <Route path={SUMMARY.path} component={SUMMARY.getComponent} />
              <Route path={INVOICES.path} component={INVOICES.getComponent} />
              <Route path={PAYMENT.path} component={PAYMENT.getComponent} />
              <TabPanel />
              <TabPanel />
              <TabPanel />
              {!moonshotOutage && isPaymentMethodCapabilityEnabled && (
                <TabPanel />
              )}
            </Tabs>
          </Base>
          {
            <div>
              {props.isAddPaymentDrawerOpen && (
                <PaymentDrawer
                  startProcessNextivaBill={props.startProcessNextivaBill}
                  addGlobalError={props.addGlobalError}
                  setVantivResponseEmpty={props.setVantivResponseEmpty}
                  setVantivResponse={props.setVantivResponse}
                  vantivResponse={props.vantivResponse}
                  corpAcctNbr={corpAcctNbr}
                  startAddPaymentMethod={props.startAddPaymentMethod}
                  onClose={props.toggleAddPaymentDrawer}
                  paymentTypeObject={props.paymentTypeObject}
                  clearPaymentTypeObject={props.clearPaymentTypeObject}
                  togglePaymentDrawer={props.togglePaymentDrawer}
                  startAddDefaultPaymentMethod={
                    props.startAddDefaultPaymentMethod
                  }
                  startSetDefaultPayment={props.startSetDefaultPayment}
                  startAddNextivaPlan={props.startAddNextivaPlan}
                  startAddNextivaBill={props.startAddNextivaBill}
                  startAddSummaryInvoices={props.startAddSummaryInvoices}
                  startAddNextInvoice={props.startAddNextInvoice}
                  billToSiteNumber={props.activeLocation.locationId}
                  addAlertDetails={props.addAlertDetails}
                  setPaymentDisabled={props.setPaymentDisabled}
                  refreshBillingInfo={refreshBillingInfo}
                  isSummaryPage={props.isSummaryPage}
                />
              )}
              {props.isPaymentDrawerOpen && (
                <MakePayment
                  onClose={props.togglePaymentDrawer}
                  paymentMethods={props.paymentMethods}
                  corpAcctNbr={corpAcctNbr}
                  startDeleteCreditCard={props.startDeleteCreditCard}
                  startSetDefaultPayment={props.startSetDefaultPayment}
                  startAddDefaultPaymentMethod={
                    props.startAddDefaultPaymentMethod
                  }
                  toggleAddPaymentDrawer={props.toggleAddPaymentDrawer}
                  defaultPaymentMethod={props.defaultPaymentMethod}
                  amount={props.amount}
                  addPaymentTypeObject={props.addPaymentTypeObject}
                  clearPaymentTypeObject={props.clearPaymentTypeObject}
                  paymentTypeObject={props.paymentTypeObject}
                  startProcessNextivaBill={props.startProcessNextivaBill}
                  startAddNextivaPlan={props.startAddNextivaPlan}
                  startAddNextivaBill={props.startAddNextivaBill}
                  startAddSummaryInvoices={props.startAddSummaryInvoices}
                  startAddNextInvoice={props.startAddNextInvoice}
                  billToSiteNumber={props.activeLocation.locationId}
                  makePaymentFlags={props.makePaymentFlags}
                  addAlertDetails={props.addAlertDetails}
                  refreshBillingInfo={refreshBillingInfo}
                  setSummaryPageFlag={props.setSummaryPageFlag}
                />
              )}
            </div>
          }
        </>
      );
    } else {
      history.push('/forbidden');
    }
  };

  return <>{renderApp()}</>;
};

BillingLanding.defaultProps = {
  roles: {
    roles: [],
    user: {},
  },
};

BillingLanding.propTypes = {
  data: PropTypes.object,
  location: PropTypes.shape({ pathname: PropTypes.string }),
  addGlobalError: PropTypes.func.isRequired,
  startAddPaymentMethod: PropTypes.func.isRequired,
  vantivResponse: PropTypes.object.isRequired,
  setVantivResponse: PropTypes.func.isRequired,
  setVantivResponseEmpty: PropTypes.func.isRequired,
  toggleAddPaymentDrawer: PropTypes.func.isRequired,
  isAddPaymentDrawerOpen: PropTypes.bool.isRequired,
  isPaymentDrawerOpen: PropTypes.bool.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  startAddDefaultPaymentMethod: PropTypes.func.isRequired,
  startDeleteCreditCard: PropTypes.func.isRequired,
  startSetDefaultPayment: PropTypes.func.isRequired,
  togglePaymentDrawer: PropTypes.func.isRequired,
  defaultPaymentMethod: PropTypes.object.isRequired,
  amount: PropTypes.object.isRequired,
  addPaymentTypeObject: PropTypes.func.isRequired,
  clearPaymentTypeObject: PropTypes.func.isRequired,
  paymentTypeObject: PropTypes.object.isRequired,
  startProcessNextivaBill: PropTypes.func.isRequired,
  error: PropTypes.object.isRequired,
  clearGlobalError: PropTypes.func.isRequired,
  clearGlobalWarning: PropTypes.func.isRequired,
  globalWarning: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPaymentMethodCapabilityEnabled: PropTypes.bool.isRequired,
  startAddNextivaPlan: PropTypes.func.isRequired,
  startAddNextivaBill: PropTypes.func.isRequired,
  startAddSummaryInvoices: PropTypes.func.isRequired,
  startAddNextInvoice: PropTypes.func.isRequired,
  locations: PropTypes.array.isRequired,
  addActiveLocation: PropTypes.func.isRequired,
  activeLocation: PropTypes.object.isRequired,
  customError: PropTypes.string.isRequired,
  clearCustomError: PropTypes.func.isRequired,
  makePaymentFlags: PropTypes.object.isRequired,
  clearAlertDetails: PropTypes.func.isRequired,
  addAlertDetails: PropTypes.func.isRequired,
  alertDetails: PropTypes.object.isRequired,
  setPaymentDisabled: PropTypes.func.isRequired,
  setPaymentMethodCapabilityEnabled: PropTypes.func.isRequired,
  clearAlerts: PropTypes.func.isRequired,
  setSummaryPageFlag: PropTypes.func.isRequired,
  isSummaryPage: PropTypes.bool.isRequired,
};

export default withRouter(BillingLanding);
